import { useEffect, useState, useCallback, useRef } from "react";
import { X, Plus, Camera, Trash2, Loader2, Link2, Copy, Check, GripVertical, ChevronUp, ChevronDown, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const STATUS_META = {
  upcoming: ["Upcoming", "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"],
  in_progress: ["In progress", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  complete: ["Complete", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
};
const backendBase = process.env.REACT_APP_BACKEND_URL || "";

function StagePhoto({ jobId, stage, photo, onDelete }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/portal/jobs/${jobId}/stages/${stage.id}/photos/${photo.id}`, { responseType: "blob" })
      .then((res) => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [jobId, stage.id, photo.id]);
  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-[#1e3d3a]/6" data-testid={`stage-photo-${photo.id}`}>
      {src ? <img src={src} alt={photo.filename} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><ImageIcon className="h-5 w-5 text-[#1e3d3a]/25" /></div>}
      <button onClick={() => onDelete(photo.id)} className="absolute top-1 right-1 rounded-full bg-black/55 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`stage-photo-del-${photo.id}`}><Trash2 className="h-3 w-3" /></button>
    </div>
  );
}

export default function JobProgress({ job, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStage, setNewStage] = useState({ title: "", note: "", date: "", status: "upcoming" });
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingTo, setUploadingTo] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);
  const uploadStage = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/portal/jobs/${job.id}/progress`); setData(data); }
    catch { toast.error("Couldn't load job progress."); }
    finally { setLoading(false); }
  }, [job.id]);
  useEffect(() => { load(); }, [load]);

  const addStage = async () => {
    if (!newStage.title.trim()) { toast.error("Give the stage a title."); return; }
    setBusy(true);
    try { await api.post(`/portal/jobs/${job.id}/stages`, newStage); setNewStage({ title: "", note: "", date: "", status: "upcoming" }); setAdding(false); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add stage."); }
    finally { setBusy(false); }
  };
  const updateStage = async (sid, patch) => {
    try { await api.patch(`/portal/jobs/${job.id}/stages/${sid}`, patch); await load(); }
    catch { toast.error("Couldn't update stage."); }
  };
  const deleteStage = async (sid) => {
    if (!window.confirm("Delete this stage and its photos?")) return;
    try { await api.delete(`/portal/jobs/${job.id}/stages/${sid}`); await load(); }
    catch { toast.error("Couldn't delete stage."); }
  };
  const moveStage = async (idx, dir) => {
    const stages = [...data.stages];
    const j = idx + dir;
    if (j < 0 || j >= stages.length) return;
    [stages[idx], stages[j]] = [stages[j], stages[idx]];
    setData({ ...data, stages });
    try { await api.post(`/portal/jobs/${job.id}/stages/reorder`, { order: stages.map((s) => s.id) }); }
    catch { toast.error("Couldn't reorder."); load(); }
  };
  const pickPhoto = (sid) => { uploadStage.current = sid; fileRef.current?.click(); };
  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const sid = uploadStage.current;
    setUploadingTo(sid);
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try { await api.post(`/portal/jobs/${job.id}/stages/${sid}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`); }
    }
    setUploadingTo(null);
    await load();
  };
  const delPhoto = async (sid, pid) => {
    try { await api.delete(`/portal/jobs/${job.id}/stages/${sid}/photos/${pid}`); await load(); }
    catch { toast.error("Couldn't delete photo."); }
  };
  const toggleShare = async () => {
    const enabled = !data.share.enabled;
    try { const { data: res } = await api.post(`/portal/jobs/${job.id}/share`, { enabled }); setData({ ...data, share: res }); }
    catch { toast.error("Couldn't update share link."); }
  };
  const shareUrl = data?.share?.token ? `${window.location.origin}/job/${data.share.token}` : "";
  const copyLink = () => { navigator.clipboard?.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-3 sm:p-6 overflow-y-auto" data-testid="job-progress-modal">
      <div className="relative w-full max-w-3xl rounded-2xl bg-[#f4f1e9] shadow-2xl my-4">
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPhotos} data-testid="stage-photo-input" />
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl bg-[#1e3d3a] px-5 py-4 text-white">
          <div className="min-w-0">
            <div className="font-head text-lg truncate">{job.name} · Progress</div>
            <div className="text-[11px] text-white/60">{job.client_name || "—"} · {job.status || "In progress"}</div>
          </div>
          <button onClick={onClose} data-testid="job-progress-close" className="rounded-full p-1.5 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Share link */}
          <div className="rounded-xl border border-[#1e3d3a]/12 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm text-[#1e3d3a]"><Link2 className="h-4 w-4 text-[#c9a961]" /> Shareable client link</div>
              <button onClick={toggleShare} data-testid="job-share-toggle" className={`rounded-full px-3 py-1 text-xs font-semibold ${data?.share?.enabled ? "bg-[#2f7d5b]/15 text-[#2f7d5b]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>{data?.share?.enabled ? "On" : "Off"}</button>
            </div>
            {data?.share?.enabled && (
              <div className="mt-2 flex items-center gap-2">
                <input readOnly value={shareUrl} data-testid="job-share-url" className="flex-1 rounded-lg border border-[#1e3d3a]/12 bg-[#f7f5ef] px-2.5 py-1.5 text-xs text-[#1e3d3a]/70" />
                <button onClick={copyLink} className="inline-flex items-center gap-1 rounded-lg bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d]">{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : "Copy"}</button>
              </div>
            )}
            <p className="mt-1.5 text-[11px] text-[#1e3d3a]/45">Turn on to give your customer a no-login page that updates as you add stages and photos.</p>
          </div>

          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#c9a961]" /></div>
          ) : (
            <>
              {/* Stages */}
              {(data.stages || []).map((s, idx) => {
                const [label, cls] = STATUS_META[s.status] || STATUS_META.upcoming;
                return (
                  <div key={s.id} data-testid={`stage-${s.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-white p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-semibold text-[#c9a961]">Stage {idx + 1}</span>
                          <span className="font-semibold text-[#1e3d3a] truncate">{s.title}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${cls}`}>{label}</span>
                        </div>
                        {s.date && <div className="text-[11px] text-[#1e3d3a]/45 mt-0.5">{s.date}</div>}
                        {s.note && <p className="text-[13px] text-[#1e3d3a]/75 mt-1">{s.note}</p>}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button onClick={() => moveStage(idx, -1)} className="p-1 text-[#1e3d3a]/35 hover:text-[#a3823f]" data-testid={`stage-up-${s.id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                        <button onClick={() => moveStage(idx, 1)} className="p-1 text-[#1e3d3a]/35 hover:text-[#a3823f]" data-testid={`stage-down-${s.id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteStage(s.id)} className="p-1 text-[#1e3d3a]/35 hover:text-[#b23a48]" data-testid={`stage-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    {/* Status quick-set + photos */}
                    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                      {Object.entries(STATUS_META).map(([k, [lbl]]) => (
                        <button key={k} onClick={() => updateStage(s.id, { status: k })} data-testid={`stage-status-${s.id}-${k}`}
                          className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium border transition-colors ${s.status === k ? "border-[#c9a961] bg-[#c9a961]/15 text-[#8a6d2f]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/55 hover:border-[#c9a961]"}`}>{lbl}</button>
                      ))}
                      <button onClick={() => pickPhoto(s.id)} data-testid={`stage-add-photo-${s.id}`} className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] px-3 py-1 text-[11px] font-semibold text-white hover:bg-[#2a534d]">
                        {uploadingTo === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Add photos
                      </button>
                    </div>
                    {(s.photos || []).length > 0 && (
                      <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {s.photos.map((p) => <StagePhoto key={p.id} jobId={job.id} stage={s} photo={p} onDelete={(pid) => delPhoto(s.id, pid)} />)}
                      </div>
                    )}
                  </div>
                );
              })}

              {data.stages.length === 0 && !adding && (
                <div className="py-8 text-center text-sm text-[#1e3d3a]/45">No stages yet. Add your first stage (e.g. "Groundbreaking").</div>
              )}

              {/* Add stage */}
              {adding ? (
                <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/5 p-4 space-y-2" data-testid="stage-add-form">
                  <input autoFocus placeholder="Stage title (e.g. Foundation)" value={newStage.title} onChange={(e) => setNewStage({ ...newStage, title: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-3 py-2 text-sm" data-testid="stage-title-input" />
                  <textarea placeholder="Short note (optional)" value={newStage.note} onChange={(e) => setNewStage({ ...newStage, note: e.target.value })} className="w-full rounded-lg border border-[#1e3d3a]/15 px-3 py-2 text-sm h-16 resize-none" />
                  <div className="flex items-center gap-2">
                    <input type="date" value={newStage.date} onChange={(e) => setNewStage({ ...newStage, date: e.target.value })} className="rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm" />
                    <select value={newStage.status} onChange={(e) => setNewStage({ ...newStage, status: e.target.value })} className="rounded-lg border border-[#1e3d3a]/15 px-2 py-1.5 text-sm bg-white">
                      <option value="upcoming">Upcoming</option><option value="in_progress">In progress</option><option value="complete">Complete</option>
                    </select>
                    <button onClick={addStage} disabled={busy} data-testid="stage-save" className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add</button>
                    <button onClick={() => setAdding(false)} className="text-sm text-[#1e3d3a]/50 hover:text-[#1e3d3a]">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setAdding(true)} data-testid="stage-add-btn" className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-[#1e3d3a]/25 py-3 text-sm font-medium text-[#1e3d3a]/60 hover:border-[#c9a961] hover:text-[#a3823f]"><Plus className="h-4 w-4" /> Add a stage</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
